import { action, computed, makeObservable, observable } from 'mobx'
import { trackedAction } from 'mobx-async'
import { computedFn } from 'mobx-utils'

import {
  BookingLeadViewStatus,
  DiscountKind,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert, formatDate } from '@avantstay/backoffice-core'

import { storeHolder as globalFastTapeChartStore } from '@new/__global/components/FastTapeChart/stores/TapeChartStore'
import {
  parseQuotesToPreviewEmail,
  parseQuotesToPreviewHomesListEmail,
  parseQuotesToPreviewNoPriceEmail,
} from '@new/domains/bookingsTapeChart/components/CreateQuoteForm/components/QuotesEmailPreview/QuotesEmailPreview.utils'
import {
  CreateQuoteFormProps,
  EmailType,
  QuotesFormView,
} from '@new/domains/bookingsTapeChart/components/CreateQuoteForm/CreateQuoteForm.types'
import {
  parseNonBrandedQuotesValues,
  parseNoPriceQuotesValues,
  parseRegularQuotesValues,
} from '@new/domains/bookingsTapeChart/components/CreateQuoteForm/CreateQuoteForm.utils'
import { HomeFilterIds } from '@new/domains/fastBookingsTapeChart/config/filtersConfig'
import { fastBookingsTapeChartFiltersStore } from '@new/domains/fastBookingsTapeChart/stores/FastBookingsTapeChartFiltersStore'
import { TapeChartCreateTapeStore } from '@new/domains/fastBookingsTapeChart/stores/TapeChartCreateTapeStore'
import { TapeChartQuotesStore } from '@new/domains/fastBookingsTapeChart/stores/TapeChartQuotesStore'

import {
  getHomeDataForQuotes,
  getPreBookingFloorPrice,
  getPreBookingPriceBreakdown,
} from '@/services/arriere/modules/bookingPrices'
import {
  getHomesListQuotesEmail,
  getRegularQuotesEmail,
  getTravelAgentQuotesEmail,
} from '@arriere/modules/messages'
import {
  createBookingQuote,
  sendQuoteHomeList,
  sendQuoteTravelAgent,
} from '@arriere/modules/quotes'
import { trackError, trackEvent } from '@tracking/customEventReport'

class _CreateQuoteFormStore {
  quotesFormView = QuotesFormView.formContent

  quoteParams = computedFn(() => {
    const { availableDates, filteringArgs } = fastBookingsTapeChartFiltersStore
    const { tapeContextPeriod } = TapeChartCreateTapeStore
    const guestsFilter = filteringArgs[HomeFilterIds.Guests]
    const petsFilter = filteringArgs[HomeFilterIds.MinPets] as number

    const fromDate = availableDates ? formatDate(availableDates.start) : tapeContextPeriod?.fromDate
    const toDate = availableDates ? formatDate(availableDates.end) : tapeContextPeriod?.toDate

    return {
      from: fromDate,
      until: toDate,
      guests: guestsFilter?.from || 1,
      pets: petsFilter || 0,
    }
  })

  constructor() {
    makeObservable(this, {
      quotesFormView: observable,
      homesPricingForQuote: computed,
      setQuotesFormView: action.bound,
      resetQuotesForm: action.bound,
    })

    this.getQuotesPreview = trackedAction<any>(this.getQuotesPreview.bind(this))
    this.createQuote = trackedAction<any>(this.createQuote.bind(this))
  }

  private homesForQuotes() {
    const { selectionQuotesHomeIds, homesForQuotesPreBookingPrice } = TapeChartQuotesStore
    const { listingsForQuotesPreBookingPrice } = globalFastTapeChartStore.store.quotesStore
    return (homesForQuotesPreBookingPrice ?? listingsForQuotesPreBookingPrice)?.filter(home =>
      selectionQuotesHomeIds?.includes(home.homeId),
    )
  }

  get homesPricingForQuote() {
    const params = this.quoteParams()

    const homesForQuotes = this.homesForQuotes()

    async function getHomePriceData(homeId: string) {
      try {
        const homeData = await getHomeDataForQuotes(homeId)

        if (params?.from && params?.until) {
          const { from, until } = params

          const homePrices = await getPreBookingPriceBreakdown({ ...params, homeId, from, until })
          const floorPrices = await getPreBookingFloorPrice({
            homeId,
            from: params.from,
            until: params.until,
          })
          const floorPriceSubtotal =
            floorPrices?.reduce((total, item) => {
              return total + item.price
            }, 0) ?? 0

          const nightsSubtotal =
            homePrices?.data?.nights?.reduce((total, item) => {
              return total + (item.price || 0)
            }, 0) ?? 0

          // Leased homes have floorPrice set as 0, in that case we use sum of nightly rates subtotal
          const maxDiscount = nightsSubtotal - floorPriceSubtotal

          const maxStay = homesForQuotes?.find(home => home.homeId === homeId)?.maxStay
          const minStay = homesForQuotes?.find(home => home.homeId === homeId)?.minStay

          return {
            ...homeData,
            ...homePrices.data,
            // This quotePrice will be manipulated to create Quote
            quotePrice: homePrices.data?.guestView.totalOfStay.withDiscount * 100,
            quotePriceBeforeTaxes:
              homePrices.data?.guestView?.preBookingTotalBeforeTaxes?.withDiscount,
            maxDiscount,
            maxStay,
            minStay,
          }
        }
      } catch (error) {
        throw new Error(`Error getting home price data: ${error}`)
      }
    }

    async function getHomeDataWithoutPrice(homeId?: string) {
      if (homeId) {
        try {
          const homeData = await getHomeDataForQuotes(homeId)
          return homeData
        } catch (error) {
          throw new Error(`Error getting home price data: ${error}`)
        }
      }
    }

    if (homesForQuotes?.length) {
      return Promise.all(homesForQuotes?.map(home => getHomePriceData(home.homeId)))
    }

    if (TapeChartQuotesStore.selectionQuotesHomeIds) {
      return Promise.all(
        TapeChartQuotesStore.selectionQuotesHomeIds?.map(homeId => getHomeDataWithoutPrice(homeId)),
      )
    }

    return Promise.resolve(undefined)
  }

  getCreateQuoteDefaultValues = computedFn(async () => {
    const { from, guests, pets, until } = this.quoteParams()

    const defaultDiscount = {
      discountKind: DiscountKind.absolute,
      discountAmount: 0,
    }

    const hasDates = !!from && !!until

    return this.homesPricingForQuote.then(result => ({
      checkIn: from,
      checkOut: until,
      salesFrom: formatDate(new Date()),
      emailType: hasDates ? EmailType.QUOTE_WITH_PRICE : EmailType.QUOTE_WITHOUT_PRICE,
      guests,
      pets,
      status: BookingLeadViewStatus.quoteSent,
      homes: result?.map(home => ({
        isHomeSelected: true,
        discount: { ...defaultDiscount },
        id: home?.id ?? '', // This fixes a type error on the form, since home will always have id it's just an override to prevent the build from failing
        ...home,
      })),
    }))
  })

  setQuotesFormView(view: QuotesFormView) {
    this.quotesFormView = view
  }

  resetQuotesForm() {
    this.setQuotesFormView(QuotesFormView.formContent)
  }

  async createQuote({
    values,
    onSuccess,
  }: {
    values: CreateQuoteFormProps
    onSuccess?: VoidFunction
  }) {
    const successMessage = 'Quote created with success!'
    const errorMessage = 'Error while trying to create quote'

    const handleSuccess = () => {
      Alert.Success(successMessage)
      trackEvent('Create Quote', { values })
      onSuccess?.()
    }

    try {
      const { emailType, contact } = values

      if (emailType === EmailType.QUOTE_WITHOUT_PRICE) {
        const noPriceQuoteValues = parseNoPriceQuotesValues(values)

        noPriceQuoteValues &&
          (await sendQuoteHomeList({
            ...noPriceQuoteValues,
            fullName: contact?.fullName || '',
          }).then(response => {
            if (response) {
              handleSuccess()
            } else throw new Error(errorMessage)
          }))
      } else if (emailType === EmailType.WITHOUT_BRANDING_AND_PRICE) {
        const nonBrandedQuoteValues = parseNonBrandedQuotesValues(values)

        nonBrandedQuoteValues &&
          (await sendQuoteTravelAgent({
            ...nonBrandedQuoteValues,
          }).then(response => {
            if (response) {
              handleSuccess()
            } else throw new Error(errorMessage)
          }))
      } else {
        const hasPricing =
          emailType === EmailType.QUOTE_WITH_PRICE ||
          emailType === EmailType.QUOTE_WITH_PRICE_WITHOUT_TAXES
        const regularQuotesValues = parseRegularQuotesValues(
          values,
          emailType === EmailType.QUOTE_WITH_PRICE_WITHOUT_TAXES,
        )
        if (hasPricing && regularQuotesValues) {
          await createBookingQuote(regularQuotesValues).then(response => {
            if (response) {
              handleSuccess()
            } else throw new Error(errorMessage)
          })
        }
      }
    } catch (error) {
      Alert.Error(`Quotes: ${error}`)
      trackError('Quotes', { error, values })
    }
  }

  async getQuotesPreview(values: CreateQuoteFormProps) {
    const { emailType } = values

    const hasPricing =
      emailType === EmailType.QUOTE_WITH_PRICE ||
      emailType === EmailType.QUOTE_WITH_PRICE_WITHOUT_TAXES
    if (hasPricing) {
      const parseValues = parseQuotesToPreviewEmail(
        values,
        emailType === EmailType.QUOTE_WITH_PRICE_WITHOUT_TAXES,
      )

      const response = await getRegularQuotesEmail(parseValues)

      trackEvent('Quotes: generate preview with price', {
        quoteValues: parseValues,
        templateResponse: response,
      })

      return response.body
    }

    if (emailType === EmailType.QUOTE_WITHOUT_PRICE) {
      const parseValues = parseQuotesToPreviewHomesListEmail(values)

      const response = await getHomesListQuotesEmail(parseValues)

      trackEvent('Quotes: generate preview without price', {
        quoteValues: parseValues,
        templateResponse: response,
      })

      return response.body
    }

    if (emailType === EmailType.WITHOUT_BRANDING_AND_PRICE) {
      const parseValues = parseQuotesToPreviewNoPriceEmail(values)
      const response = await getTravelAgentQuotesEmail(parseValues)

      trackEvent('Quotes: generate preview without branding and price', {
        quoteValues: parseValues,
        templateResponse: response,
      })

      return response.body
    }
  }
}

export const CreateQuoteFormStore = new _CreateQuoteFormStore()
